import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { GetHistoryByArhId } from "./DataServices/AutomaticResponsesDataServices";
import { IAutomaticResponseHistory } from "./Models";

interface MemberGuarantorInfo {
  id: string;
  name: string;
  isGuarantor: boolean;
}

interface AutoResponseDetailsinfo {
  id: string;
  confirmed: boolean | null;
  sentDate: string | null;
  startTime: string | null;
  endTime: string | null;
  memberAndGaurantorInfo: MemberGuarantorInfo[];
}

interface Props {
  arhid: string; // Single ARH ID
  info: AutoResponseDetailsinfo;
  mappingInfo?: {
    totalMembers: number;
    mappedMembers: number;
  };
  appointmentStatus?: string;
  getExternalFormStatus: () => { status: string; color: string };
  getAppfolioStatus: () => { status: string; color: string };
  isMandatory?: boolean;
  // When true, renders a condensed single-row stepper + inline status
  // badges instead of the full multi-section layout.
  compact?: boolean;
}

interface GroupedARHData {
  title: string;
  listingID: string;
  latestDate: Date;
  arhHistory: IAutomaticResponseHistory[];
}

interface StatusFlowStep {
  label: string;
  active: boolean;
  description: string;
}

const MemberStatusFlow: React.FC<Props> = ({
  arhid,
  info,
  mappingInfo,
  appointmentStatus,
  getExternalFormStatus,
  getAppfolioStatus,
  isMandatory = false,
  compact = false,
}) => {
  const [groupedData, setGroupedData] = useState<GroupedARHData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const formatAppointmentTiming = (
    startTime?: string | Date | null,
    endTime?: string | Date | null,
  ) => {
    if (!startTime || !endTime) return null;

    const slotStart =
      typeof startTime === "string"
        ? DateTime.fromISO(startTime)
        : DateTime.fromJSDate(startTime);
    const slotEnd =
      typeof endTime === "string"
        ? DateTime.fromISO(endTime)
        : DateTime.fromJSDate(endTime);

    if (!slotStart.isValid || !slotEnd.isValid) return null;

    return `${slotStart.toFormat("MMM dd, yyyy h:mm a")} - ${slotEnd.toFormat("h:mm a")} `;
  };

  useEffect(() => {
    const fetchdata = async () => {
      try {
        setLoading(true);
        const data = await GetHistoryByArhId(arhid);

        if (data && Array.isArray(data)) {
          // Filter to only show the apartment for the current inquiry
          const grouped = groupDataByTitle(data).filter((g) =>
            g.arhHistory.some((h) => h.id === arhid),
          );
          setGroupedData(grouped);

          if (grouped.length > 0) {
            setExpandedItems(new Set([`${grouped[0].title}-0`]));
          }
        }
      } catch (e) {
        console.error("Error fetching data", e);
      } finally {
        setLoading(false);
      }
    };

    fetchdata();
  }, [arhid]);

  const groupDataByTitle = (
    data: IAutomaticResponseHistory[],
  ): GroupedARHData[] => {
    // Group by title
    const grouped = data.reduce(
      (acc, item) => {
        const key = item.title || "Unknown Apartment";
        if (!acc[key]) {
          acc[key] = {
            title: key,
            listingID: item.listingID,
            latestDate: new Date(item.sentDate),
            arhHistory: [],
          };
        }
        acc[key].arhHistory.push(item);

        // Update latest date if this item is more recent
        const itemDate = new Date(item.sentDate);
        if (itemDate > acc[key].latestDate) {
          acc[key].latestDate = itemDate;
        }

        return acc;
      },
      {} as Record<string, GroupedARHData>,
    );

    // Convert to array and sort by latest date (newest first)
    return Object.values(grouped).sort(
      (a, b) => b.latestDate.getTime() - a.latestDate.getTime(),
    );
  };

  const getStatusForARH = (arhHistory: IAutomaticResponseHistory[]) => {
    // Get the most recent ARH entry for status calculation
    const latestARH = arhHistory.sort(
      (a, b) => new Date(b.sentDate).getTime() - new Date(a.sentDate).getTime(),
    )[0];

    if (!latestARH) return { currentStep: 0, statusFlow: [], latestARH: null };

    const slotStart =
      typeof latestARH.startTime === "string"
        ? DateTime.fromISO(latestARH.startTime)
        : DateTime.fromJSDate(latestARH.startTime);
    const nowEST = DateTime.local();
    const confirmed = latestARH.confirmed === true;
    const seen = confirmed && nowEST > slotStart;

    const statusFlow: StatusFlowStep[] = [
      {
        label: "Inquired",
        active: !confirmed,
        description: "Initial inquiry received",
      },
      {
        label: "Confirmed",
        active: confirmed && !seen,
        description: "Viewing appointment confirmed",
      },
      {
        label: "Seen",
        active: seen,
        description: "Property viewing completed",
      },
    ];

    const getCurrentStepIndex = () => {
      if (seen) return 2;
      if (confirmed) return 1;
      return 0;
    };

    return {
      currentStep: getCurrentStepIndex(),
      statusFlow,
      latestARH,
    };
  };

  const getAppointmentStatusLabel = (status?: string) => {
    if (status === "Timing") return "Timing";
    if (status === "NoSlots") return "No slot Available right now";
    if (status === "MailNotSentYet") return "Appointment Mail Not Sent Yet";
    return null;
  };

  const getAppointmentStatusTone = (status?: string) => {
    if (status === "Timing")
      return "bg-green-100 text-green-800 border-green-200";
    if (status === "NoSlots")
      return "bg-orange-50 text-orange-700 border-orange-200";
    if (status === "MailNotSentYet")
      return "bg-red-50 text-red-700 border-red-200";
    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const renderStatusFlow = (
    currentStep: number,
    statusFlow: StatusFlowStep[],
    apartmentTitle: string,
  ) => (
    <div className="relative mb-4">
      {/* Connecting Line */}
      <div className="absolute top-6 left-6 right-6 h-0.5 bg-gray-200 z-0">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out"
          style={{
            width: `${(currentStep / (statusFlow.length - 1)) * 100}%`,
          }}
        ></div>
      </div>

      {/* Steps container */}
      <div className="flex items-center justify-between relative">
        {statusFlow.map((step, index) => (
          <div
            key={`${apartmentTitle}-${index}`}
            className="flex flex-col items-center relative"
          >
            <div
              className={`
                w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 shadow-lg
                relative
                ${
                  index <= currentStep
                    ? "bg-blue-500 text-white border-2 border-blue-600"
                    : "bg-gray-200 text-gray-500 border-2 border-gray-300"
                }
              `}
            >
              {index <= currentStep ? "✓" : ""}
            </div>

            <div
              className={`
                mt-3 text-center transition-all duration-300
                ${
                  index <= currentStep
                    ? "text-blue-600 font-semibold"
                    : "text-gray-400"
                }
              `}
            >
              <div className="font-medium">{step.label}</div>
              <div className="text-xs mt-1 max-w-24 leading-tight">
                <div>{step.description}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="text-gray-600">Loading ARH data...</div>
      </div>
    );
  }

  // If no grouped data or only one apartment, show original single flow
  if (groupedData.length <= 1) {
    const confirmed = info.confirmed === true;
    const memberList = info.memberAndGaurantorInfo || [];
    const appStarted = memberList.length > 0;

    const slotStart = info.startTime ? DateTime.fromISO(info.startTime) : null;
    const slotEnd = info.endTime ? DateTime.fromISO(info.endTime) : null;
    const nowEST = DateTime.local();
    const seen = info.confirmed === true && !!slotStart && nowEST > slotStart;
    const timingLabel = formatAppointmentTiming(info.startTime, info.endTime);

    const statusFlow: StatusFlowStep[] = [
      {
        label: "Inquired",
        active: !confirmed,
        description: "Initial inquiry received",
      },
      {
        label: "Confirmed",
        active: confirmed && !seen,
        description: "Viewing appointment confirmed",
      },
      {
        label: "Seen",
        active: seen,
        description: "Property viewing completed",
      },
    ];

    const getCurrentStepIndex = () => {
      if (seen) return 2;
      if (confirmed) return 1;
      return 0;
    };

    const currentStep = getCurrentStepIndex();

    if (compact) {
      const appointmentAlert =
        appointmentStatus === "MailNotSentYet"
          ? "Appointment mail not sent yet"
          : appointmentStatus === "NoSlots"
            ? "No slots available right now"
            : null;

      return (
        <div className="space-y-2">
          {/* Mini stepper */}
          <div className="flex items-center">
            {statusFlow.map((step, index) => (
              <React.Fragment key={step.label}>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      index <= currentStep
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {index <= currentStep ? "✓" : ""}
                  </div>
                  <span
                    className={`text-xs ${
                      index <= currentStep
                        ? "text-blue-600 font-semibold"
                        : "text-gray-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {index < statusFlow.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      index < currentStep ? "bg-blue-400" : "bg-gray-200"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Inline status badges */}
          <div className="flex flex-wrap items-center gap-1.5 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">
              {statusFlow[currentStep].label}
            </span>
            {appStarted && (
              <>
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${getExternalFormStatus().color}`}
                >
                  External: {getExternalFormStatus().status}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full font-medium ${getAppfolioStatus().color}`}
                >
                  Appfolio: {getAppfolioStatus().status}
                </span>
              </>
            )}
            {timingLabel && (
              <span className="text-gray-500">{timingLabel}</span>
            )}
          </div>

          {appointmentAlert && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
              {appointmentAlert}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Enhanced Status Flow */}
        <div>
          <h4 className="font-bold text-xl text-gray-900 mb-4">
            Group Status Flow
          </h4>
          {renderStatusFlow(currentStep, statusFlow, "single")}

          {/* Current Status Details */}
          <div className="rounded-lg p-3 border border-gray-200">
            <div className="flex items-center gap-3">
              <div>
                <h5 className="font-semibold text-gray-900">
                  Current Status: {statusFlow[currentStep].label}
                </h5>
                <p className="text-gray-600 text-sm mt-1">
                  {statusFlow[currentStep].description}
                </p>
                {timingLabel && (
                  <p className="text-gray-700 text-sm mt-1 font-medium">
                    Timing: {timingLabel}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Pending / No Slots alert - match style used elsewhere */}
        {appointmentStatus === "MailNotSentYet" && (
          <div className="rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50 px-4 py-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10A8 8 0 114 3.08V2a1 1 0 10-2 0v4a1 1 0 001 1h4a1 1 0 100-2H5.64A6 6 0 1016 10a1 1 0 102 0zm-7-3a1 1 0 10-2 0v4a1 1 0 001.447.894l2-1a1 1 0 10-.894-1.788l-.553.277V7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-rose-800">
                  Appointment Pending
                </p>
                <p className="mt-1 text-sm text-rose-700">
                  Appointment mail is not sent yet. Showing progress will appear
                  after mail is sent.
                </p>
              </div>
            </div>
          </div>
        )}

        {appointmentStatus === "NoSlots" && (
          <div className="rounded-xl border border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50 px-4 py-3 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10A8 8 0 114 3.08V2a1 1 0 10-2 0v4a1 1 0 001 1h4a1 1 0 100-2H5.64A6 6 0 1016 10a1 1 0 102 0zm-7-3a1 1 0 10-2 0v4a1 1 0 001.447.894l2-1a1 1 0 10-.894-1.788l-.553.277V7z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-rose-800">
                  No slots available
                </p>
                <p className="mt-1 text-sm text-rose-700">
                  There are currently no available slots. We will send you an
                  appointment mail as soon as slots become available.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Application Process Info */}
        {appStarted && (
          <div className="rounded-lg p-4 border border-gray-200">
            <h4 className="font-bold text-lg text-gray-900 mb-3">
              Application Process
            </h4>

            {/* Application Status */}
            <div className="mb-2 space-y-2 ">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  External Status:
                </span>
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    getExternalFormStatus().color
                  }`}
                >
                  {getExternalFormStatus().status}
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-gray-700">
                  Appfolio Status:
                </span>
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    getAppfolioStatus().color
                  }`}
                >
                  {getAppfolioStatus().status}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show multiple apartment flows
  return (
    <div className="space-y-6">
      {isMandatory && getAppointmentStatusLabel(appointmentStatus) && (
        <div className="rounded-lg border bg-white p-4 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            Appointment Status
          </div>
          <div
            className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${getAppointmentStatusTone(appointmentStatus)}`}
          >
            {getAppointmentStatusLabel(appointmentStatus)}
          </div>
        </div>
      )}

      {/* Total Applied Count - Only show if multiple or specifically requested, but user wants only current */}
      {groupedData.length > 1 && (
        <div className="mb-6">
          <h3 className="font-bold text-2xl text-gray-900">
            Total Applied: {groupedData.length}
          </h3>
        </div>
      )}

      {/* Application Boxes */}
      <div className="space-y-4">
        {groupedData.map((group, index) => {
          const { currentStep, statusFlow, latestARH } = getStatusForARH(
            group.arhHistory,
          );
          const itemKey = `${group.title}-${index}`;
          const isExpanded = expandedItems.has(itemKey);

          // Get current status
          const currentStatus = statusFlow[currentStep]?.label || "Unknown";
          const timingLabel = formatAppointmentTiming(
            latestARH?.startTime,
            latestARH?.endTime,
          );

          const toggleExpanded = (key: string) => {
            setExpandedItems((prev) => {
              const newSet = new Set(prev);
              if (newSet.has(key)) {
                newSet.delete(key);
              } else {
                newSet.add(key);
              }
              return newSet;
            });
          };
          return (
            <div
              key={itemKey}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Clickable Header Box */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                onClick={() => toggleExpanded(itemKey)}
              >
                <div className="flex items-center space-x-4 min-w-0 flex-1">
                  {/* Apartment Name with fixed width */}
                  <div className="w-48 truncate" title={group.title}>
                    <span className="font-semibold text-gray-900">
                      {group.title}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    <span
                      className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                        currentStatus === "Seen"
                          ? "bg-green-100 text-green-800"
                          : currentStatus === "Confirmed"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {currentStatus}
                    </span>
                  </div>

                  {group.arhHistory.some((h) => h.id === arhid) && (
                    <div className="flex-shrink-0">
                      <span className="inline-flex px-2 py-0.5 text-[10px] font-bold rounded-md bg-blue-600 text-white uppercase tracking-wider">
                        Current
                      </span>
                    </div>
                  )}
                </div>

                {/* Expand/Collapse Icon */}
                <div className="flex-shrink-0">
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {/* Expandable Content */}
              {isExpanded && (
                <div className="border-t border-gray-200 p-6">
                  {/* Apartment Header */}
                  <div className="mb-6 pb-4 border-b border-gray-200">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-xl text-gray-900">
                          {group.title}
                        </h4>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {group.arhHistory.length} Application
                          {group.arhHistory.length !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Status Flow */}
                  <div className="mb-6">
                    <h5 className="font-semibold text-lg text-gray-900 mb-4">
                      Status Progress
                    </h5>

                    {getAppointmentStatusLabel(appointmentStatus) && (
                      <div className="mb-4 rounded-lg border px-4 py-3">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">
                          Appointment Status
                        </div>
                        <div
                          className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold ${getAppointmentStatusTone(appointmentStatus)}`}
                        >
                          {getAppointmentStatusLabel(appointmentStatus)}
                        </div>
                      </div>
                    )}

                    {renderStatusFlow(currentStep, statusFlow, group.title)}

                    {/* Current Status Details */}
                    <div className="rounded-lg p-4 border border-gray-200 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div>
                          <h6 className="font-semibold text-gray-900">
                            Current Status: {currentStatus}
                          </h6>
                          <p className="text-gray-600 text-sm mt-1">
                            {statusFlow[currentStep]?.description}
                          </p>
                          {timingLabel && (
                            <p className="text-gray-700 text-sm mt-1 font-medium">
                              Timing: {timingLabel}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MemberStatusFlow;
